/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import Title from "../basic/Title";
import Text from "../basic/Text";
import Button from "../basic/Button";
import { Stack } from "../basic/Stack";

export type StatusPageProps = {
  code?: string | number;
  title: string;
  description?: string;
  actionLabel?: string;
  onAction?: () => void;
  titleClassName?: string;
  illustration?: React.ReactNode;
  testId?: string;
};

/**
 * Shared full-page status layout for error, not-found, and similar feedback pages.
 * Composes design system primitives with proper token-based spacing and typography.
 * Logo is intentionally omitted -- it is already rendered by the GlobalHeader.
 */
export function StatusPage({ code, title, description, actionLabel, onAction, titleClassName, illustration, testId }: StatusPageProps) {
  return (
    <div data-testid={testId} className="flex items-center justify-center min-h-[calc(100vh-var(--header-height))]">
      <Stack gap="var(--space-8)" align="center">
        {illustration}
        {code != null && (
          <Title order={1}>{code}</Title>
        )}
        <Stack gap="var(--space-3)" align="center">
          <Title order={2} className={titleClassName}>{title}</Title>
          {description && (
            <Text className="text-(--text-secondary) max-w-[65ch]" ta="center">{description}</Text>
          )}
        </Stack>
        {actionLabel && (
          <Button variant="filled" size="cta" onClick={onAction}>
            {actionLabel}
          </Button>
        )}
      </Stack>
    </div>
  );
}
